import React, { useState, useEffect, useRef } from 'react';
import {
    Card,
    Input,
    Button,
    List,
    Avatar,
    Typography,
    Space,
    Divider,
    Empty,
    Spin,
    message as antMessage,
    Badge,
    Tooltip
} from 'antd';
import {
    SendOutlined,
    MessageOutlined,
    UserOutlined,
    PhoneOutlined,
    MailOutlined,
    CalendarOutlined,
    EnvironmentOutlined,
    PaperClipOutlined,
    SmileOutlined
} from '@ant-design/icons';
import { useAuth } from '../../../contexts/AuthContext';
import api from '../../../services/api';
import hybridChatService from '../../../services/hybridChat';
import dayjs from 'dayjs';
import { CheckOutlined } from '@ant-design/icons';
import soundNotificationService from '../../../services/soundNotification';

const { TextArea } = Input;
const { Text: AntText, Title } = Typography;

function ChatInterface({ leadId, onClose }) {
    const { user } = useAuth();
    const [lead, setLead] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [newMessage, setNewMessage] = useState('');
    const [unreadCount, setUnreadCount] = useState(0);
    const [otherTyping, setOtherTyping] = useState(false);
    const messagesEndRef = useRef(null);
    useEffect(() => {
        if (leadId) {
            loadLeadDetails();
            loadMessages();
            setupChatService();
        }

        return () => {
            hybridChatService.disconnect();
        };
    }, [leadId]);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const loadLeadDetails = async () => {
        try {
            const response = await api.getLead(leadId);
            if (response.success) {
                setLead(response.data);
            }
        } catch (error) {
            antMessage.error('Failed to load lead details');
        }
    };

    const loadMessages = async () => {
        try {
            setLoading(true);
            const response = await api.getLeadMessages(leadId);
            if (response.success) {
                setMessages(response.data || []);
                setUnreadCount(0);
                // Mark messages as read when opening the thread
                try { await api.markLeadMessagesAsRead(leadId); } catch(_) {}
            }
        } catch (error) {
            antMessage.error('Failed to load messages');
        } finally {
            setLoading(false);
        }
    };

    const setupChatService = async () => {
        try {
            await hybridChatService.initialize(leadId);
            
            // Listen for new messages
            hybridChatService.onNewMessage((messages) => {
                console.log('New messages received:', messages);
                setMessages(prev => [...prev, ...messages]);
                setUnreadCount(prev => prev + 1);
                scrollToBottom();
                
                // Play sound for messages not sent by current user
                messages.forEach(message => {
                    if (message?.sender_type && message.sender_type !== user?.user_type) {
                        try {
                            soundNotificationService.playNotificationSound();
                        } catch (_) {}
                    }
                });
            });

            // Listen for typing indicators
            hybridChatService.onTyping((data) => {
                if (data?.user_id && data.user_id !== user?.id) {
                    setOtherTyping(!!data.is_typing);
                    if (data.is_typing) {
                        setTimeout(() => setOtherTyping(false), 2500);
                    }
                }
            });

            // Listen for connection changes
            hybridChatService.onConnectionChange((state) => {
                console.log('Chat connection state:', state);
            });

            console.log(`Chat service initialized using: ${hybridChatService.getServiceType()}`);
        } catch (error) {
            console.error('Failed to setup chat service:', error);
        }
    };

    const handleSendMessage = async () => {
        if (!newMessage.trim()) return;

        try {
            setSending(true);
            const response = await api.sendLeadMessage(leadId, {
                message: newMessage.trim()
            });

            if (response.success) {
                setMessages(prev => [...prev, response.data]);
                setNewMessage('');
                scrollToBottom();
            } else {
                antMessage.error('Failed to send message');
            }
        } catch (error) {
            antMessage.error('Failed to send message');
        } finally {
            setSending(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleTyping = (isTyping) => {
        hybridChatService.sendTyping(isTyping);
    };

    const getMessageAlignment = (message) => {
        return message.sender_type === user?.user_type ? 'flex-end' : 'flex-start';
    };

    const getMessageStyle = (message) => {
        return message.sender_type === user?.user_type ? 'primary' : 'default';
    };

    const formatMessageTime = (timestamp) => {
        return dayjs(timestamp).format('HH:mm');
    };

    const getOtherParticipant = () => {
        if (!lead) return null;
        
        if (user?.user_type === 'vendor') {
            return lead.user;
        } else {
            return lead.vendor;
        }
    };

    const otherParticipant = getOtherParticipant();

    // Group messages by date for separators
    const formatDateHeader = (ts) => dayjs(ts).format('ddd, MMM D');
    const shouldShowDateSeparator = (prevMsg, currMsg) => {
        if (!prevMsg) return true;
        const prev = dayjs(prevMsg.created_at).startOf('day');
        const curr = dayjs(currMsg.created_at).startOf('day');
        return !prev.isSame(curr);
    };

    return (
        <div className="h-full flex flex-col bg-white">
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white p-4 flex items-center justify-between shadow-lg">
                <div className="flex items-center space-x-3">
                    <Avatar 
                        size={48} 
                        icon={<UserOutlined />}
                        src={otherParticipant?.avatar}
                        className="ring-2 ring-white ring-opacity-30"
                    />
                    <div>
                        <Title level={5} className="!mb-0 !text-white">
                            {otherParticipant?.name || otherParticipant?.business_name}
                        </Title>
                        <AntText className="text-orange-100 text-sm">
                            {lead?.event_type} • {lead?.event_date}
                        </AntText>
                    </div>
                </div>
                <div className="flex items-center space-x-2">
                    {unreadCount > 0 && (
                        <Badge count={unreadCount} size="small" className="bg-red-500" />
                    )}
                    <Button 
                        type="text" 
                        onClick={onClose}
                        className="text-white hover:bg-white hover:bg-opacity-20 rounded-full w-8 h-8 flex items-center justify-center"
                    >
                        ×
                    </Button>
                </div>
            </div>

            {/* Lead Details */}
            {lead && (
                <div className="bg-gray-50 border-b border-gray-200 p-4">
                    <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="flex items-center space-x-2 text-gray-600">
                            <CalendarOutlined className="text-orange-500" />
                            <AntText className="font-medium">{lead.event_date} at {lead.event_time}</AntText>
                        </div>
                        <div className="flex items-center space-x-2 text-gray-600">
                            <UserOutlined className="text-green-500" />
                            <AntText className="font-medium">{lead.guest_count} guests</AntText>
                        </div>
                        <div className="flex items-center space-x-2 text-gray-600 col-span-2">
                            <EnvironmentOutlined className="text-purple-500" />
                            <AntText className="truncate font-medium">{lead.venue}</AntText>
                        </div>
                        <div className="flex items-center space-x-2 text-gray-600 col-span-2">
                            <PhoneOutlined className="text-orange-500" />
                            <AntText className="font-medium">{lead.contact_phone}</AntText>
                        </div>
                    </div>
                </div>
            )}

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-white">
                {loading ? (
                    <div className="flex justify-center items-center h-32">
                        <Spin size="large" />
                    </div>
                ) : messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-32 text-gray-500">
                        <MessageOutlined className="text-4xl mb-2" />
                        <AntText type="secondary">No messages yet</AntText>
                        <AntText type="secondary" className="text-xs">Start the conversation!</AntText>
                    </div>
                ) : (
                    messages.map((msg, index) => {
                        const showDate = shouldShowDateSeparator(messages[index - 1], msg);
                        const mine = msg.sender_type === user?.user_type;
                        return (
                            <React.Fragment key={msg.id || index}>
                                {showDate && (
                                    <div className="text-center text-[11px] text-gray-500 my-2">
                                        {formatDateHeader(msg.created_at)}
                                    </div>
                                )}
                                <div className="flex" style={{ justifyContent: getMessageAlignment(msg) }}>
                                    <div
                                        className={`max-w-xs lg:max-w-md px-4 py-3 rounded-2xl shadow-sm ${
                                            mine
                                                ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-br-md'
                                                : 'bg-gray-100 text-gray-800 border border-gray-200 rounded-bl-md'
                                        }`}
                                    >
                                        <div className="text-sm leading-relaxed">{msg.message}</div>
                                        <div className={`flex items-center gap-1 text-[10px] mt-2 ${mine ? 'text-orange-100' : 'text-gray-500'}`}>
                                            <span>{formatMessageTime(msg.created_at)}</span>
                                            {mine && (
                                                <span className="ml-1">
                                                    {msg.is_read ? '✓✓' : '✓'}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </React.Fragment>
                        );
                    })
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <div className="bg-white border-t border-gray-200 p-4 shadow-lg">
                <div className="flex items-end space-x-3">
                    <Button
                        type="text"
                        icon={<PaperClipOutlined />}
                        className="text-gray-500 hover:text-orange-500"
                        title="Attach (coming soon)"
                    />
                    <TextArea
                        value={newMessage}
                        onChange={(e) => {
                            setNewMessage(e.target.value);
                            handleTyping(e.target.value.length > 0);
                        }}
                        onKeyPress={handleKeyPress}
                        placeholder="Type your message..."
                        autoSize={{ minRows: 1, maxRows: 4 }}
                        className="flex-1 rounded-2xl border-gray-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 resize-none"
                        style={{ 
                            borderRadius: '24px',
                            padding: '12px 16px',
                            border: '1px solid #d1d5db',
                            fontSize: '14px'
                        }}
                    />
                    <Button
                        type="text"
                        icon={<SmileOutlined />}
                        className="text-gray-500 hover:text-orange-500"
                        title="Emoji (coming soon)"
                    />
                    <Button
                        type="primary"
                        icon={<SendOutlined />}
                        onClick={handleSendMessage}
                        loading={sending}
                        disabled={!newMessage.trim()}
                        className="rounded-full w-12 h-12 flex items-center justify-center bg-gradient-to-r from-orange-500 to-red-500 border-0 shadow-lg hover:shadow-xl transition-all duration-200"
                        style={{ minWidth: '48px' }}
                    />
                </div>
                {otherTyping && (
                    <div className="text-[11px] text-gray-500 mt-2">Typing…</div>
                )}
            </div>
        </div>
    );
}

export default ChatInterface;
